import type {
  SafetyDashboardScanHistoryItem,
  SafetyDashboardResponse,
  SafetyDashboardRow,
  SafetyReportRecord,
} from '../../services/reportsApi';
import { externalSafetyReviewPath } from '../externalReview/externalReviewCopy';
import { extractStoredEffectiveMeasurementBasis } from '../safetyScanReport/canonicalStoredMeasurementBasis';

export type SpatialOperationsHistoricalScan = {
  reportId: string;
  status: string;
  windowStart: string | null;
  windowEnd: string | null;
};

export type SpatialOperationsScanHistoryView =
  | { status: 'loading'; scans: []; latest: null; historyHref: string }
  | { status: 'error'; scans: []; latest: null; historyHref: string }
  | {
      status: 'ready';
      scans: SpatialOperationsHistoricalScan[];
      latest: SpatialOperationsHistoricalScan | null;
      historyHref: string;
    };

function text(value: unknown): string {
  return String(value || '').trim();
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function reportTimestamp(report: SpatialOperationsHistoricalScan): number {
  const parsed = new Date(report.windowEnd || report.windowStart || '').getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function persistedStatusLabel(value: unknown): string {
  const persisted = text(value);
  if (!persisted) return 'Status unavailable';
  return `${persisted.charAt(0).toUpperCase()}${persisted.slice(1)}`;
}

export function safetyScanHistoryWorkspacePath(siteId: string): string {
  const query = new URLSearchParams({ tab: 'scans' });
  if (text(siteId)) query.set('site_id', text(siteId));
  return `/acceptance/reports?${query.toString()}`;
}

/**
 * Reuse the Safety Control Workspace's persisted Scan history inventory.
 * Report identity is resolved only after opening each exact report_id.
 */
export function safetyDashboardScanReportIds(dashboard: SafetyDashboardResponse): string[] {
  const ids = new Set<string>();
  const add = (value: unknown) => {
    const id = text(value);
    if (id) ids.add(id);
  };
  (dashboard.workflows || []).forEach((row: SafetyDashboardRow) => {
    [row.current_scan, row.latest_signed_scan, ...(row.scan_history || [])].forEach((scan) => {
      add(scan?.report_id || scan?.scan_id);
    });
    add(row.report_id);
    add(row.active_draft_report_id);
    add(row.latest_finalized_report_id);
  });
  return Array.from(ids);
}

/** Complete dashboard history, admitted only by the report revision's frozen condition identity. */
export function selectExactConditionDashboardScanHistory(
  dashboard: SafetyDashboardResponse,
  conditionId: string,
): SpatialOperationsHistoricalScan[] {
  const expected = text(conditionId);
  if (!expected) return [];
  const scans = new Map<string, SpatialOperationsHistoricalScan>();
  (dashboard.workflows || []).forEach((row: SafetyDashboardRow) => {
    const items: SafetyDashboardScanHistoryItem[] = [
      ...(row.current_scan ? [row.current_scan] : []),
      ...(row.latest_signed_scan ? [row.latest_signed_scan] : []),
      ...(row.scan_history || []),
    ];
    items.forEach((item) => {
      if (!(item.frozen_condition_ids || []).some((id) => text(id) === expected)) return;
      const reportId = text(item.report_id || item.scan_id);
      if (!reportId) return;
      scans.set(reportId, {
        reportId,
        status: persistedStatusLabel(item.status),
        windowStart: text(item.scope_start) || null,
        windowEnd: text(item.scope_end) || null,
      });
    });
  });
  return Array.from(scans.values()).sort((left, right) => (
    reportTimestamp(right) - reportTimestamp(left)
    || right.reportId.localeCompare(left.reportId)
  ));
}

/** Exact persisted/frozen condition identity only; no label, time, scope, or family fallback. */
export function reportHasExactConditionId(
  report: SafetyReportRecord,
  conditionId: string,
): boolean {
  const expected = text(conditionId);
  if (!expected) return false;
  const basis = extractStoredEffectiveMeasurementBasis(report as Record<string, unknown>);
  const components = basis && Array.isArray(basis.components) ? basis.components : [];
  return components.some((component) => (
    text(asRecord(asRecord(component).condition).condition_id) === expected
  ));
}

export function selectExactConditionScanHistory(
  reports: SafetyReportRecord[],
  conditionId: string,
): SpatialOperationsHistoricalScan[] {
  return reports
    .filter((report) => reportHasExactConditionId(report, conditionId))
    .flatMap((report) => {
      const reportId = text(report.report_id);
      if (!reportId) return [];
      return [{
        reportId,
        status: persistedStatusLabel(report.status),
        windowStart: text(report.window_start_utc) || null,
        windowEnd: text(report.window_end_utc) || null,
      }];
    })
    .sort((left, right) => (
      reportTimestamp(right) - reportTimestamp(left)
      || right.reportId.localeCompare(left.reportId)
    ));
}

export function presentSpatialOperationsScanHistory(args: {
  reports: SafetyReportRecord[];
  conditionId: string;
  siteId: string;
}): SpatialOperationsScanHistoryView {
  const scans = selectExactConditionScanHistory(args.reports, args.conditionId);
  return {
    status: 'ready',
    scans,
    latest: scans[0] || null,
    historyHref: safetyScanHistoryWorkspacePath(args.siteId),
  };
}

export function presentSpatialOperationsDashboardScanHistory(args: {
  dashboard: SafetyDashboardResponse;
  conditionId: string;
  siteId: string;
}): SpatialOperationsScanHistoryView {
  const scans = selectExactConditionDashboardScanHistory(args.dashboard, args.conditionId);
  return {
    status: 'ready',
    scans,
    latest: scans[0] || null,
    historyHref: safetyScanHistoryWorkspacePath(args.siteId),
  };
}

export function historicalScanOpenPath(reportId: string): string {
  return externalSafetyReviewPath(reportId);
}
